import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, ListChecks, PackagePlus, Truck, Upload } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { StockImportWizard } from '../components/stock-import/StockImportWizard';

/**
 * Purchases → "Import facture d'achat" (2026-10-13). The invoice of a supplier with no API
 * (Autodoc…), received into stock and kept as that supplier's purchase in the register — the
 * counterpart of the purchase orders the API suppliers get from Réapprovisionnement. Same wizard
 * as the warehouse's stock import, locked to receipts and asking who the goods are from.
 */
export default function PurchaseImport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const steps = [
    { icon: Upload, title: t('purchases.import.step1'), body: t('purchases.import.step1Body') },
    { icon: Truck, title: t('purchases.import.step2'), body: t('purchases.import.step2Body') },
    { icon: ListChecks, title: t('purchases.import.step3'), body: t('purchases.import.step3Body') },
  ];

  return (
    <div>
      <PageHeader
        title={t('purchases.import.title')}
        description={t('purchases.import.description')}
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            <Upload className="h-4 w-4" /> {t('purchases.import.start')}
          </Button>
        }
      />

      <Card className="p-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
          <FileText className="h-8 w-8 text-muted-foreground" />
          <p className="mx-auto max-w-lg text-xs text-muted-foreground">
            {t('purchases.import.body')}
          </p>
          <ol className="grid w-full gap-3 text-left sm:grid-cols-3">
            {steps.map((s, i) => (
              <li key={i} className="rounded-lg border bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
                    {i + 1}
                  </span>
                  <s.icon className="h-4 w-4 text-muted-foreground" />
                  {s.title}
                </div>
                <p className="text-xs text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => setOpen(true)}>
              <PackagePlus className="h-4 w-4" /> {t('purchases.import.start')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/purchasing')}>
              {t('purchases.import.toRegister')}
            </Button>
          </div>
        </div>
      </Card>

      <StockImportWizard open={open} onClose={() => setOpen(false)} purpose="PURCHASE" />
    </div>
  );
}
